
import Vue from "vue";
import { DocumentUploadService } from "@/features/Documents/DocumentUploadService";
import { PlatformDocumentUploadService } from "@/features/PlatformDocuments/PlatformDocumentUploadService";
import AppError from "@/shared/components/AppError.vue";
import UploadedPlatformDocumentMetadata from "@/features/PlatformDocuments/UploadedPlatformDocumentMetadata";
import PlatformDocumentMetadata from "@/features/PlatformDocuments/PlatformDocumentMetadata";
import ConfigurationOptions from "@/features/Configuration/ConfigurationOptions";

interface FileToUpload {
    temporaryId: number;
    name: string;
    progress: number;
    uploadedID: string | null; // Compliance: Document metadata ID. Platform: Temporary ID
}

/**
 * A drag-and-drop file upload component.
 * Place this anywhere files need to be uploaded and respond to events when users add files and they are uploaded
 * Borrowing heavily from concepts here:
 * https://george-hadjigeorgiou97.medium.com/step-by-step-custom-drag-drop-upload-component-in-vuetify-vue-2-43c99794643d
 */
const FileUpload = Vue.extend({
    name: "FileUpload",
    components: {
        AppError,
    },
    props: {
        // Whether documents uploaded through this component should go to the CQMS Platform (true) or Compliance (false)
        forPlatform: {
            type: Boolean,
            default: false,
        },
        // Required for Platform uploads, not used for Compliance uploads
        workspaceID: {
            type: String,
            default: null,
        },
        multiple: {
            type: Boolean,
            default: true,
        },
        maxConcurrentUploads: {
            type: Number,
            default: 3,
        },
    },
    data: () => ({
        dragover: false,
        files: [] as FileToUpload[],
        errorMessage: "",
        showError: false,
        uploadsInProgress: 0,
        acceptedFileSizeBytes: (Vue.prototype.$configuration as ConfigurationOptions)?.uploads?.maxFileSizeLimitInBytes ?? 15728640,
        acceptedFileTypesMimeTypes: (Vue.prototype.$configuration as ConfigurationOptions)?.uploads?.acceptableMimeTypes ?? [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "text/csv",
            "application/vnd.ms-powerpoint",
            "application/vnd.openxmlformats-officedocument.presentationml.presentation",
            "image/jpeg",
            "image/png",
            "image/pjpeg",
            "text/plain",
            "message/rfc822",
            "application/zip",
            "application/x-zip-compressed",
        ],
        acceptedFileTypesExtensions: (Vue.prototype.$configuration as ConfigurationOptions)?.uploads?.acceptableExtensions ?? [
            ".pdf",
            ".doc",
            ".docx",
            ".xls",
            ".xlsx",
            ".csv",
            ".ppt",
            ".pptx",
            ".jpg",
            ".png",
            ".txt",
            ".eml",
            ".zip",
        ],
    }),
    methods: {
        //
        // Ref methods
        //
        reset() {
            this.files = [];
        },
        //
        // Component methods
        //
        onDrop(e: DragEvent) {
            // Called when we drop files into the dropzone
            console.log("Files Dropped");
            this.dragover = false;
            if (e.dataTransfer === null) {
                this.errorMessage = "Cannot upload document";
                this.showError = true;
            } else if (!this.multiple && e.dataTransfer.files.length > 1) {
                this.errorMessage = "Please drop one file at a time";
                this.showError = true;
            } else {
                Array.from(e.dataTransfer.files).forEach(newFile => this.uploadFile(newFile));
            }
        },
        onFilesSelected(files: File[]) {
            if (files) {
                // Called when we select files from the file picker
                console.log("Files selected from picker");
                files.forEach(newFile => this.uploadFile(newFile));
            }
        },
        async uploadFile(file: File) {
            // Validate concurrent uploads, max file size, and file type
            if (this.files.filter(f => f.progress < 100).length >= this.maxConcurrentUploads) {
                this.errorMessage = `A maximum of ${this.maxConcurrentUploads} files can be uploaded at a time. Please wait until others have finished.`;
                this.showError = true;
                return;
            }
            if (file.size > this.acceptedFileSizeBytes) {
                this.errorMessage = "File is too large.";
                this.showError = true;
                return;
            }
            if (file.type != undefined && (file.type == "" || !this.acceptedFileTypesMimeTypes.find(t => t.localeCompare(file.type) === 0))) {
                this.errorMessage = "Allowed file types are " + this.acceptedFileTypesExtensions.join(", ");
                this.showError = true;
                return;
            }

            const fileInProgress: FileToUpload = {
                temporaryId: Math.floor(Math.random() * 0xffffffff),
                name: file.name,
                progress: 0,
                uploadedID: null,
            };
            this.files.push(fileInProgress);

            try {
                this.uploadsInProgress++;
                if (this.forPlatform) {
                    this.$emit("uploadChanged", true);
                    const platformDocumentUploadService = Vue.prototype.$platformDocumentUploadService as PlatformDocumentUploadService;
                    const response = await platformDocumentUploadService.uploadDocument(file, this.workspaceID, progress => {
                        fileInProgress.progress = progress * 100;
                    });
                    fileInProgress.uploadedID = response.documentID;

                    var uploadedPlatformDocumentMetadata = new UploadedPlatformDocumentMetadata();
                    uploadedPlatformDocumentMetadata.uploadedPlatformDocument = response;
                    uploadedPlatformDocumentMetadata.uploadedPlatformDocumentMetaData = new PlatformDocumentMetadata();
                    uploadedPlatformDocumentMetadata.uploadedPlatformDocumentMetaData.name = fileInProgress.name;
                    uploadedPlatformDocumentMetadata.uploadedPlatformDocumentMetaData.contentType = file.type;
                    this.$emit("documentUploaded", uploadedPlatformDocumentMetadata);
                } else {
                    this.$emit("uploadChanged", true);
                    const documentUploadService = Vue.prototype.$documentUploadService as DocumentUploadService;
                    const response = await documentUploadService.uploadDocument(file, progress => {
                        fileInProgress.progress = progress * 100;
                    });
                    fileInProgress.uploadedID = response.documentMetadataId;
                    this.$emit("documentUploaded", response.documentMetadataId);
                }
            } catch (e) {
                this.errorMessage = `Failed to upload document ${file.name}`;
                this.showError = true;
            } finally {
                this.uploadsInProgress--;
                if (this.uploadsInProgress == 0) {
                    this.$emit("uploadChanged", false);
                }
            }
        },
        removeFile(uploadedID: string) {
            // remove file from this component's display
            this.files = this.files.filter(f => f.uploadedID != uploadedID);
            // emit event to remove file from comment
            this.$emit("documentRemoved", uploadedID);
        },
    },
});

export default FileUpload;
export type FileUploadRef = InstanceType<typeof FileUpload>; // Used if parents need to use ref functions
